import { useState } from 'react';
import type { GongFlowStepDetailWire } from '@shared/flow/gong-flow-catalog';
import type { StepDraft } from '@shared/flow/enrollment';
import { RichHtmlField } from '../../ui/RichHtmlField';
import { RichTextBodyField } from '../../ui/RichTextBodyField';
import { AUTOFILL_IGNORE_PROPS } from '../../../lib/autofill';
import type { StepOverrideSnapshot } from './state/step-override-drafts';
import type { StagedStepCopy } from './state/staged-play-config';
import { useStepOverrideRowModel } from './useStepOverrideRowModel';

/**
 * Portable per-step rich-HTML override editor (bead .13, Gong Flow Play +
 * Queue redesign). PORTABLE by design (spec §7 rule 4): props are
 * `(steps, overrides, onChangeOverride, onResetOverride, localDraftFor,
 * saveErrorFor)` — no `EnrollmentItem` anywhere in this file's types, so a
 * future producer fork (a second surface that assigns Gong flows outside the
 * enroll queue) can mount this unchanged. The mount owner
 * (`EnrollmentItemEditor`) reads `EnrollmentItem`/context and passes down
 * plain values. The one import from `./state/step-override-drafts` is the
 * draft store's SHAPE and its read hook (`useStepOverrideEntry`, see below) —
 * no store instance, no `EnrollQueueContext`, so the portability claim
 * stands: a fork supplies its own subscribe/read functions.
 *
 * Structure/copy match the locked prototype
 * (`docs/flow/gong-flow-play-and-queue-redesign-prototype.html:572-605`).
 *
 * Basic save-and-send (plan 2026-08-19-004): edits are LOCAL until Send. A
 * row's displayed value derives `localDraft ?? persistedOverride ??
 * stepDefault`; every settled keystroke commits the FULL effective
 * Subject+Body snapshot to the caller's draft store (`onChangeOverride`),
 * which issues NO network request — Send's save phase
 * (`EnrollQueueContext.saveStepOverrides`) is the one write moment.
 *
 * Draft State Under SSE compliance note: the letter of the CLAUDE.md rule
 * names `useDraftState`, which this component deliberately no longer uses —
 * the hazard the rule exists for (a refetch reseeding over unsaved
 * keystrokes) cannot occur here, because the local draft store is never
 * reseeded from server data: an entry exists only from an explicit rep
 * action and is removed only by save-success / reset / item departure. The
 * derived-value rule above IS the re-render-proof pattern for this design.
 * `RichHtmlField` stays (contentEditable rule).
 */

/**
 * The three step-type primitives this component's model reads
 * (`EMAIL_STEP_TYPES`, `stepHasSubjectField`, `GONG_STEP_TYPE_LABELS`) now
 * live in the sibling leaf `./gong-step-types` — their definitions and copy
 * are unchanged, only their file moved. They had to: plan 2026-09-30-002 U3
 * lifts this component's row model into `./useStepOverrideRowModel`, which
 * reads all three, and leaving them here would make that hook and this
 * component an `import/no-cycle` error.
 *
 * Re-exported so every pre-existing import site keeps working unedited
 * (`EnrollQueueContext.tsx` imports `EMAIL_STEP_TYPES` from this module) and
 * so the V2 rail's `StepRow` can take `GONG_STEP_TYPE_LABELS` from this
 * module's public surface as planned (D28).
 */
export { EMAIL_STEP_TYPES, stepHasSubjectField, GONG_STEP_TYPE_LABELS } from './gong-step-types';

/**
 * The full effective Subject+Body snapshot a row commits on every edit
 * (plan 2026-08-19-004, Decision #3: full-snapshot capture, never partial
 * patches — kills the partial-merge bug class by construction and makes
 * "use flow default → re-edit" trivially correct). Never carries `null`:
 * the editor only ever commits displayed text; "use flow default" travels
 * through `onResetOverride`, not a null-field snapshot.
 *
 * Declared in `./state/step-override-drafts` (the draft store owns the shape
 * it stores) and re-exported here because this component's prop contract is
 * the type's public surface for every consumer.
 */
export type { StepOverrideSnapshot };

export interface StepOverrideEditorProps {
  /** Plain id, NOT an `EnrollmentItem` — only used to compose `RichHtmlField`'s per-step seedKey. */
  itemId: number;
  /** The flow's default per-step copy (`GET /api/flow/gong/flows/:flowId/steps`), 1-based `step_order`, ascending. */
  steps: GongFlowStepDetailWire[];
  /** This item's persisted overrides (`EnrollmentItem.step_drafts`) — sparse, one entry per overridden step. */
  overrides: StepDraft[];
  disabled?: boolean;
  disabledTitle?: string;
  /** Fires on every settled keystroke for one step with the FULL effective snapshot; the caller stores it locally (no network). */
  onChangeOverride: (stepNumber: number, stepType: string, values: StepOverrideSnapshot) => void;
  /**
   * "Use flow default" — local intent until Send. Whether a `DELETE` will be
   * needed at save time is the STORE's call (finding #3): this component's
   * `override` prop derives from render-time queue data, which is stale in
   * the window between a PUT landing and the refetch — so no
   * persisted-override boolean travels through here.
   */
  onResetOverride: (stepNumber: number, stepType: string) => void;
  /**
   * The caller's unsaved entry for one step (`values: null` = reset-to-default
   * intent), or `null` when untouched. Typing-lag fix (2026-08-20): the caller
   * (`EnrollQueueContext.stepOverrideDraftFor`, bound to this item's id) is
   * referentially STABLE and reads the draft store directly — it is no
   * longer threaded down as an already-resolved value; `StepOverrideRow`
   * calls it itself as the read half of `useStepOverrideEntry`, paired with
   * `subscribeStep` below, so an edit re-renders only the one row whose key
   * changed. Must return a referentially stable value while nothing changed.
   */
  localDraftFor: (stepNumber: number) => { values: StepOverrideSnapshot | null } | null;
  /** Send-time save failure message for one step, or `null`. Renders as "Failed to save — retry". Same stability note as `localDraftFor`. */
  saveErrorFor: (stepNumber: number) => string | null;
  /**
   * Typing-lag fix (2026-08-20) — the subscribe half of the pair
   * `useStepOverrideEntry` reads, for one step's entry
   * (`EnrollQueueContext.subscribeStepOverrides`, bound to this item's id).
   * One subscription per row covers BOTH `localDraftFor` and `saveErrorFor` —
   * both live in the caller's same per-key store entry.
   */
  subscribeStep: (stepNumber: number, onChange: () => void) => () => void;
  /**
   * U9 (F2c) — which body editor to render: `'rich'` mounts
   * `RichTextBodyField` (adds the bold/italic/underline/link/list toolbar,
   * scoped to exactly the server's `sanitizeStepDraftHtml` allowlist);
   * `'plain'` (the default) keeps the bare `RichHtmlField`, unchanged.
   * Optional so every pre-existing call site (this file's own tests
   * included) keeps today's plain behavior without editing. The caller
   * (`EnrollmentItemEditor`) reads this off the resolved play type's
   * `ui.body_format` wire capability (`PlayUiCapabilitiesSchema`,
   * `src/shared/flow/play-catalog.ts`) — it is never hardcoded to a play
   * type slug here, so a future second `'rich'` play type gets the toolbar
   * with no new branch in this component.
   */
  bodyFormat?: 'plain' | 'rich';
  /**
   * U5 (bulk step-override editor, plan 2026-09-08-002 Part 2, Resolved
   * Decision 6) — per-key overrides for the three strings in this component
   * that assume a SINGLE lead. Strictly additive with the same
   * zero-behavior-change-by-default contract as `bodyFormat` above and Part
   * 1's `playRowLabel`: omit the prop (every pre-existing call site) or omit
   * any individual key, and that string renders byte-identically to today.
   *
   * Exists so `BulkStepDefaultsEditor` can mount this component unchanged in
   * BATCH mode ("applied to every lead in this batch") instead of forking it
   * — the portability claim in this file's header, cashed in. Nothing about
   * lead COUNT reaches this component otherwise: it still knows only
   * `itemId` as a seedKey ingredient, and batch mode passes the sentinel
   * `-1`.
   *
   * - `header` / `headerHint` — the section heading's two halves
   *   (`Flow steps` + `— override any step for this lead (optional)`); the
   *   hint keeps its `normal-case font-normal` treatment either way.
   * - `subjectHint` — the email-only Subject field's hint (`— this lead only`).
   * - `bodyHint` — the body/notes hint's TAIL only (`enrollment-time only`).
   *   The `isEmailBody`-conditional `HTML override, ` prefix is a fact about
   *   the field, not about lead count, so it is deliberately NOT overridable
   *   and survives an override.
   * - `stepScope` — a parenthetical appended to each step expander's
   *   ACCESSIBLE NAME only (visible text untouched). See its own note on
   *   `StepOverrideEditorLabels`.
   */
  labels?: StepOverrideEditorLabels;
  /**
   * Plan 2026-09-08-002 Part 2, v3 rewrite, Key Technical Decision 2 — the
   * batch rung's data, threaded down as ONE plain-data prop rather than the
   * the batch-draft callback PAIR v2 used (deleted:
   * Devin's review on PR #1390 flagged a pair that could be half-supplied by
   * mistake and duplicated the sentinel-store subscription this v3 rewrite
   * removes entirely). Keyed `String(step.step_order)`, the same shape
   * `stepCopy[playKey]` stores it in `state/staged-play-config.ts`.
   *
   * Optional, defaulting to `undefined` — every pre-existing mount omits it
   * and renders byte-identically, since `resolveEffectiveStepValues`'s batch
   * rung is only ever consulted when a row's own lookup here resolves to a
   * value. `PlayQueueCard` reads this once per card
   * (`useStagedStepCopy(identity, playKey)`) and passes it per row, gated on
   * `isBulkFollowEligible` — the same posture `copyPrefill` already uses for
   * base copy.
   */
  batchStepCopy?: Readonly<Record<string, StagedStepCopy>>;
}

/** See `StepOverrideEditorProps.labels`. Named so the batch mount can type its own copy constant. */
export interface StepOverrideEditorLabels {
  header?: string;
  headerHint?: string;
  subjectHint?: string;
  bodyHint?: string;
  /**
   * Disambiguates this mount's step expanders from a SECOND
   * `StepOverrideEditor` mounted in the same card — which is exactly what a
   * multi-lead Gong group renders: the batch step-defaults editor plus every
   * open `AssignedRow`'s per-lead editor, all of whose expanders would
   * otherwise share the accessible name `Expand step 1 — Manual Email`.
   *
   * Appended as a parenthetical to the expander's `aria-label` ONLY — the
   * visible header row is unchanged, because the on-screen ambiguity is
   * already resolved by the section headings above each editor, while an
   * accessibility-tree consumer (a screen reader's element list, or the
   * `ab snapshot` browser-QA harness this repo's UI work is verified with)
   * sees a flat list of identically-named buttons.
   *
   * Omitted (every pre-existing call site) → the name is byte-identical to
   * today's.
   */
  stepScope?: string;
}

/**
 * Today's literals — the defaults every omitted `labels` key falls back to.
 * `stepScope` is excluded from the `satisfies` constraint deliberately: it is
 * the one key with no default, because "omitted" means "render no scope
 * parenthetical at all", not "render some other string". The constraint is
 * still `Required<…>` over the remaining keys, so adding a new copy key
 * without a default here is a compile error.
 */
export const DEFAULT_LABELS = {
  header: 'Flow steps',
  headerHint: '— override any step for this lead (optional)',
  subjectHint: '— this lead only',
  bodyHint: 'enrollment-time only',
} as const satisfies Required<Omit<StepOverrideEditorLabels, 'stepScope'>>;

function StepOverrideRow({
  itemId,
  step,
  override,
  disabled,
  disabledTitle,
  onChangeOverride,
  onResetOverride,
  localDraftFor,
  saveErrorFor,
  subscribeStep,
  bodyFormat = 'plain',
  labels,
  batchStepCopy,
}: {
  itemId: number;
  step: GongFlowStepDetailWire;
  override: StepDraft | undefined;
  disabled: boolean;
  disabledTitle?: string;
  onChangeOverride: (stepNumber: number, stepType: string, values: StepOverrideSnapshot) => void;
  onResetOverride: (stepNumber: number, stepType: string) => void;
  localDraftFor: (stepNumber: number) => { values: StepOverrideSnapshot | null } | null;
  saveErrorFor: (stepNumber: number) => string | null;
  subscribeStep: (stepNumber: number, onChange: () => void) => () => void;
  bodyFormat?: 'plain' | 'rich';
  /** U5 — see `StepOverrideEditorProps.labels`. Only `subjectHint`/`bodyHint` are read here. */
  labels?: StepOverrideEditorLabels;
  /** v3 — see `StepOverrideEditorProps.batchStepCopy`. */
  batchStepCopy?: Readonly<Record<string, StagedStepCopy>>;
}) {
  // Plan 2026-09-30-002 D6 — every SSE/re-render defence this row used to
  // carry inline now lives in `useStepOverrideRowModel` (a pure move of what
  // stood here), so the V2 Glaze row can consume the same model instead of
  // forking it. Read that hook's header before changing anything below: the
  // derivation ladder is render-time with no reseed effect, the body seed
  // bumps only on an external write or an explicit reset, and `commit`
  // records its own write so own typing never re-seeds the contentEditable.
  // This component keeps only its VIEW state (`open`) and its markup.
  const {
    effective,
    isOverridden,
    hasSubject,
    isEmailBody,
    stepTypeLabel,
    subjectPreview,
    saveError,
    bodySeedKey,
    commit,
    reset,
  } = useStepOverrideRowModel({
    itemId,
    step,
    override,
    localDraftFor,
    saveErrorFor,
    subscribeStep,
    batchStepCopy,
    onChangeOverride,
    onResetOverride,
  });
  const [open, setOpen] = useState(false);

  // U9 (F2c) — identical props for either body editor component; only
  // `bodyFormat` decides which component renders them (see the render
  // below). Declared once so the two branches can't drift out of sync.
  const bodyFieldProps = {
    value: effective.body_draft,
    seedKey: bodySeedKey,
    disabled,
    onChange: (html: string) => {
      commit({ body_draft: html });
    },
    className: 'mt-1 w-full min-h-[90px] text-[12px] px-2 py-1 rounded border border-secondary dark:border-nightshade-700 bg-white dark:bg-nightshade-800 text-content-primary dark:text-nightshade-100',
    // U1 (Gong Flow follow-up bundle bug 1 fix) — the body field's wrapper
    // was a bare <label> with no htmlFor. contentEditable divs aren't
    // labelable, so the label auto-bound to the toolbar's Bold button
    // instead (the first labelable descendant) and Chromium forwarded click
    // activation into it, destroying the caret / bolding text on
    // double-click. The wrapper below is now a plain <div>; this is the
    // editable region's real accessible name, matching the Subject
    // <label>'s own aria-label convention just above.
    'aria-label': `${isEmailBody ? 'Body' : 'Notes / script'} for step ${step.step_order}`,
  };

  return (
    <div className="rounded-md border border-secondary dark:border-nightshade-700 mb-1.5">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-label={`${open ? 'Collapse' : 'Expand'} step ${step.step_order} — ${stepTypeLabel}${labels?.stepScope ? ` (${labels.stepScope})` : ''}`}
        className="w-full flex items-center gap-2 px-2 py-1.5 text-left text-[11.5px]"
      >
        <span className="text-content-tertiary dark:text-nightshade-500 font-medium">{step.step_order}</span>
        <span className="text-content-primary dark:text-nightshade-100">
          {stepTypeLabel}
          {subjectPreview && (
            <span className="font-normal text-content-tertiary dark:text-nightshade-500"> — {subjectPreview}</span>
          )}
        </span>
        {isOverridden && (
          <span className="text-[9.5px] px-1 py-0.5 rounded bg-bg-blue-light text-content-action dark:text-blueberry-400 uppercase tracking-wide">
            OVERRIDDEN
          </span>
        )}
        <span className="ml-auto text-content-tertiary dark:text-nightshade-500">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="px-2 pb-2 grid gap-1.5">
          {hasSubject && (
            <label className="text-[11px] text-content-secondary dark:text-nightshade-400">
              Subject <span className="text-content-tertiary dark:text-nightshade-500 font-normal">{labels?.subjectHint ?? DEFAULT_LABELS.subjectHint}</span>
              <input
                type="text"
                value={effective.subject_draft}
                disabled={disabled}
                title={disabledTitle}
                onChange={e => {
                  commit({ subject_draft: e.target.value });
                }}
                aria-label={`Subject for step ${step.step_order}`}
                {...AUTOFILL_IGNORE_PROPS}
                className="mt-1 w-full text-[12px] px-2 py-1 rounded border border-secondary dark:border-nightshade-700 bg-white dark:bg-nightshade-800 text-content-primary dark:text-nightshade-100 disabled:opacity-60 disabled:cursor-not-allowed"
              />
            </label>
          )}
          {/* U1 (Gong Flow follow-up bundle bug 1 fix) — this was a bare
              <label> with no htmlFor. contentEditable divs aren't labelable,
              so nothing in here was ever validly bound to it; instead it
              auto-bound to the toolbar's Bold button (the first labelable
              descendant when bodyFormat === 'rich') and Chromium forwarded
              label-click activation into Bold, destroying the caret and
              bolding text on double-click. Plain <div> now — the editable
              region gets its own real `aria-label` via `bodyFieldProps`
              above instead. Do NOT touch the Subject <label> above this
              block: it wraps a real, labelable <input> and already carries
              its own `aria-label`. */}
          <div className="text-[11px] text-content-secondary dark:text-nightshade-400">
            {isEmailBody ? 'Body' : 'Notes / script'}{' '}
            <span className="text-content-tertiary dark:text-nightshade-500 font-normal">
              {/* U5 — `bodyHint` replaces this hint's TAIL only; the
                  `isEmailBody` prefix is a fact about the field, not about
                  lead count. See `StepOverrideEditorProps.labels`. */}
              — {isEmailBody ? 'HTML override, ' : ''}{labels?.bodyHint ?? DEFAULT_LABELS.bodyHint}
            </span>
            {/* U9 (F2c) — same props either way (drop-in compatibility is the
                whole point of RichTextBodyField's prop shape); only the
                component differs, so the shared prop object is declared once
                rather than duplicated across both branches. */}
            {bodyFormat === 'rich' ? (
              <RichTextBodyField {...bodyFieldProps} />
            ) : (
              <RichHtmlField {...bodyFieldProps} />
            )}
          </div>
          <div className="flex items-center justify-between mt-0.5">
            {/* Error-only save feedback (plan 2026-08-19-004, Decision #6):
                "Saving…/Saved ✓" described background saves that no longer
                exist — the Send button's own "Sending…" covers the save
                phase now. A failed send-time save keeps this exact label. */}
            <span aria-live="polite" className="text-[10.5px] text-content-tertiary dark:text-nightshade-500">
              {saveError ? 'Failed to save — retry' : null}
            </span>
            {isOverridden && (
              <button
                type="button"
                disabled={disabled}
                onClick={reset}
                className="text-[10.5px] px-1.5 py-0.5 rounded border border-secondary dark:border-nightshade-700 text-content-secondary dark:text-nightshade-400 hover:bg-bg-secondary disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Use flow default
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function StepOverrideEditor({
  itemId,
  steps,
  overrides,
  disabled = false,
  disabledTitle,
  onChangeOverride,
  onResetOverride,
  localDraftFor,
  saveErrorFor,
  subscribeStep,
  bodyFormat = 'plain',
  labels,
  batchStepCopy,
}: StepOverrideEditorProps) {
  const overrideByStep = new Map(overrides.map(o => [o.step_number, o]));

  return (
    <div className="mt-3">
      <div className="text-[11px] uppercase tracking-wide text-content-tertiary dark:text-nightshade-500 mb-1">
        {labels?.header ?? DEFAULT_LABELS.header}{' '}
        <span className="normal-case font-normal">{labels?.headerHint ?? DEFAULT_LABELS.headerHint}</span>
      </div>
      {steps.map(step => (
        <StepOverrideRow
          key={step.step_order}
          itemId={itemId}
          step={step}
          override={overrideByStep.get(step.step_order)}
          disabled={disabled}
          disabledTitle={disabledTitle}
          onChangeOverride={onChangeOverride}
          onResetOverride={onResetOverride}
          // Typing-lag fix (2026-08-20): pass the FUNCTIONS through
          // unresolved — `StepOverrideRow` calls them itself, inside its own
          // `useStepOverrideEntry` subscription, so THIS component (and
          // everything above it) never needs to re-render for a step edit to
          // show up.
          localDraftFor={localDraftFor}
          saveErrorFor={saveErrorFor}
          subscribeStep={subscribeStep}
          bodyFormat={bodyFormat}
          labels={labels}
          batchStepCopy={batchStepCopy}
        />
      ))}
    </div>
  );
}
